import { CheckCircledIcon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  TaskResponse,
  TaskStatus,
  TasksService,
} from "client/openapi";
import TaskViewDialog from "components/Dashboard/PeoplePanel/TaskViewDialog";
import { formatDate } from "components/Forms/ResponseDialog";
import { BubbleTone } from "components/IconBubble";
import SectionTitle from "components/SectionTitle";
import { useCallback, useContext, useEffect, useState } from "react";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { isPast } from "util/time";

/** By kind of person: where a task names them. */
const RECIPIENT = {
  [Auth0AccountRole.ME]: "student",
  [Auth0AccountRole.PARENT]: "parent",
  [Auth0AccountRole.ORG_TUTOR]: "tutor",
} as const;

const overdue = (task: TaskResponse) => isPast(new Date(task.due_date));

/**
 * The Tasks section of a student's, parent's or tutor's page, for admins and
 * tutors: the person's pending tasks (about them for an admin, assigned to
 * them for a tutor), soonest first, each opening in the task window.
 */
export default function PersonTasksSection({
  orgId,
  personType,
  personId,
}: {
  orgId: number;
  personType: Auth0AccountRole;
  personId: number;
}) {
  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);
  const isAdmin = currently_selected_role === Auth0AccountRole.ORG_ADMIN;
  const shown =
    isAdmin || currently_selected_role === Auth0AccountRole.ORG_TUTOR;
  const recipient = RECIPIENT[personType as keyof typeof RECIPIENT];
  const [tasks, setTasks] = useState<TaskResponse[]>();
  const [openTask, setOpenTask] = useState<TaskResponse>();

  // Loaded as the dashboard's Tasks view loads them.
  const load = useCallback(() => {
    if (!shown) return;
    Promise.all([
      TasksService.getTasksByAccount(),
      isAdmin
        ? TasksService.getOrgTasks({ orgId })
        : TasksService.getTasksForStudentsOfAccount(),
    ])
      .then(([accountTasks, moreTasks]) => {
        const byId = new Map(
          [...accountTasks, ...moreTasks].map((task) => [task.id, task])
        );
        setTasks(
          Array.from(byId.values())
            .filter(
              (task) =>
                task.status === TaskStatus.PENDING &&
                (isAdmin ? task.tagged_users : task.assigned_users).some(
                  (user) => user[recipient]?.id === personId
                )
            )
            .sort(
              (a, b) =>
                new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
            )
        );
      })
      .catch(() => Notifications.error("Tasks could not be loaded."));
  }, [shown, isAdmin, orgId, recipient, personId]);

  useEffect(load, [load]);

  if (!shown) return null;

  return (
    <section className="student-profile--section">
      <div className="student-profile--section-header">
        <SectionTitle icon={<CheckCircledIcon />} tone={BubbleTone.ORANGE}>
          Tasks
        </SectionTitle>
      </div>
      <div className="student-profile--section-items">
        <div className="student-profile--section-item">
          {tasks === undefined && (
            <p className="text-sm text-gray-500">Loading...</p>
          )}
          {tasks && tasks.length === 0 && (
            <p className="text-sm text-gray-600">No open tasks.</p>
          )}
          {tasks &&
            tasks.map((task) => (
              <button
                key={task.id}
                type="button"
                className="flex w-full items-center justify-between gap-3 border-b border-gray-100 py-2 text-left hover:bg-gray-50"
                onClick={() => setOpenTask(task)}
              >
                <span className="block text-sm font-medium text-gray-900">
                  {task.name}
                </span>
                <span
                  className={`text-xs whitespace-nowrap ${
                    overdue(task)
                      ? "people-tasks__due--overdue"
                      : "text-gray-500"
                  }`}
                >
                  {formatDate(task.due_date)}
                </span>
              </button>
            ))}
        </div>
      </div>

      {openTask && (
        <TaskViewDialog
          tasks={[{ ...openTask, overdue: overdue(openTask) }]}
          orgId={orgId}
          loadedTasks={tasks}
          onClose={() => {
            setOpenTask(undefined);
            load();
          }}
          // The list reloads when the window closes.
          onChanged={() => {}}
        />
      )}
    </section>
  );
}
